import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { CheckCircle2, CircleHelp, FlaskConical, ListChecks, MessageSquare, Target } from "lucide-react";

import { Chip, HERO_BACKGROUND, HeroPill, Panel, PanelTitle, ShareBar, StatGrid } from "@/components/panels";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { GHOST_BUTTON, InitiativeChip, ListRow, Meter, PRIMARY_BUTTON, RowList, SMALL_GHOST, SMALL_PRIMARY, SectionNote } from "@/components/growth/GrowthParts";
import {
  EXPERIMENT_STATUS, GOAL_KINDS, INITIATIVE_STATUS, PACE_WORDS, fmtByUnit, fmtDayShort, fmtMonth, fmtPercent, fmtStamp, fmtTarget,
  fmtUsd, fmtUsdCents, goalActual, metricsBetween, ownerWord, parseTarget, setterName, shiftDay, suggestTarget,
} from "@/growth/growthMath";
import { GROWTH_TEXT_LIMITS } from "@/growth/growthLimits";

// The growth system's popups, all on the SkillsDialog recipe: a navy header with an eyebrow, the title, one line and a
// pill; then panels built from components/panels.jsx. Nothing here fetches; pages hand in data and callbacks.

const PACE_PILL = { met: "on", on_pace: "on", slightly_behind: "warn", behind: "bad", no_data: "quiet" };
const INITIATIVE_PILL = { next: "quiet", in_progress: "busy", waiting_on_you: "warn", done: "on", parked: "quiet" };
const EXPERIMENT_PILL = { idea: "quiet", running: "busy", decided: "on", dropped: "quiet" };

export function GrowthDialog({ open, onClose, eyebrow, title, description, pill = null, children, testId, wide = false }) {
  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) onClose(); }}>
      <DialogContent
        data-testid={testId}
        className={`flex max-h-[88vh] w-[calc(100vw-24px)] ${wide ? "max-w-[880px]" : "max-w-[720px]"} flex-col gap-0 overflow-hidden rounded-[20px] border-0 bg-sm-surface p-0 text-sm-ink sm:rounded-[20px] [&>button]:right-5 [&>button]:top-5 [&>button]:text-white [&>button]:opacity-80 [&>button]:hover:opacity-100`}
      >
        <div className="shrink-0 px-6 pb-6 pr-14 pt-6 text-white md:px-7 md:pr-16" style={{ background: HERO_BACKGROUND }}>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="text-[11px] font-extrabold uppercase tracking-[0.14em] text-white/65">{eyebrow}</div>
              <DialogTitle className="mt-1.5 font-display text-[26px] font-extrabold leading-[1.1] tracking-[-0.02em] text-white [text-wrap:balance]">{title}</DialogTitle>
              <DialogDescription className="mt-2 text-[13px] text-white/75">{description}</DialogDescription>
            </div>
            {pill}
          </div>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5 md:px-6">
          <div className="flex flex-col gap-4">{children}</div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const BAR_CLASSES = ["bg-[#4690D9]", "bg-[#9cc6f0]", "bg-[#2F6DB5]", "bg-[#7fb0e4]"];

/** A goal with where its number comes from and the initiatives attached to it. */
export function GoalDialog({ open, onClose, goal, pace, contributions, agentName, onOpenInitiative, onAsk, askDisabled = false }) {
  if (!goal) return null;
  const meta = GOAL_KINDS[goal.key];
  const unit = goal.unit || meta?.unit || "count";
  const status = pace?.status || "no_data";
  const parts = (contributions?.parts || []).map((part, index) => ({
    key: part.key,
    percent: part.percent,
    barClass: part.kind === "base" ? "bg-[#003B8D]" : part.kind === "gap" ? "bg-[#F59E0B]/60" : BAR_CLASSES[index % BAR_CLASSES.length],
    legend: `${part.label} ${fmtUsd(part.amount)}`,
  }));
  const draft = `Re-plan the "${goal.label}" goal for ${fmtMonth(goal.period)}. Target ${fmtTarget(goal)}; so far ${fmtByUnit(pace?.actual, unit)} (${PACE_WORDS[status]}). Attached initiatives: ${contributions?.attached?.length ? contributions.attached.map((item) => `${item.title} (${INITIATIVE_STATUS[item.status]?.label || item.status})`).join("; ") : "none"}. Propose what to change and record the changes in the plan.`;
  return (
    <GrowthDialog open={open} onClose={onClose} testId="growth-goal-dialog" eyebrow={`Goal · ${fmtMonth(goal.period)}`} title={goal.label} description={`Target ${fmtTarget(goal)} · ${PACE_WORDS[status]}${pace?.projection != null && meta?.kind === "sum" ? ` · on pace for ${fmtByUnit(pace.projection, unit)}` : ""}`} pill={<HeroPill tone={PACE_PILL[status]} dot={status === "on_pace" || status === "met"}>{PACE_WORDS[status].toUpperCase()}</HeroPill>}>
      <Panel>
        <StatGrid stats={[
          { label: "So far", value: fmtByUnit(pace?.actual, unit), sub: pace?.percent != null ? `${Math.round(pace.percent)}% of target` : "Not measured yet" },
          { label: meta?.kind === "sum" ? "Projection" : "Now", value: fmtByUnit(pace?.projection, unit), sub: meta?.kind === "sum" ? "At this month's daily pace" : "Measured directly" },
          { label: "Target", value: fmtTarget(goal), sub: goal.setBy ? `Set by ${setterName(goal.setBy)}` : null },
        ]} />
      </Panel>
      {parts.length ? (
        <Panel>
          <PanelTitle aside={<span className="text-xs text-sm-ink/65">Estimates</span>}>Where the number comes from</PanelTitle>
          <ShareBar parts={parts} label="Contribution to the target" note={contributions.gap > 0 ? "Short at this pace" : "Covered at this pace"} />
          <p className="mt-3 text-xs leading-relaxed text-sm-ink/65">Run-rate is this month's daily pace carried to month end. Each initiative's amount is {agentName}'s estimate, stated on the initiative.</p>
        </Panel>
      ) : (
        <SectionNote>This target is measured directly, so there is no contribution bar. The initiatives below are the work {agentName} attached to it.</SectionNote>
      )}
      <Panel>
        <PanelTitle aside={<span className="text-xs text-sm-ink/65">{contributions?.attached?.length || 0} attached</span>}>Initiatives</PanelTitle>
        {contributions?.attached?.length ? (
          <RowList>
            {contributions.attached.map((item) => (
              <ListRow key={item.id} icon={ListChecks} tone={INITIATIVE_STATUS[item.status]?.tone === "busy" ? "quiet" : INITIATIVE_STATUS[item.status]?.tone || "quiet"} title={item.title} text={`${ownerWord(item.owner, agentName)}${item.expectedImpactCents ? ` · est. ${fmtUsdCents(item.expectedImpactCents)}` : ""}`} right={<InitiativeChip status={item.status} />} onClick={() => onOpenInitiative?.(item)} testId={`growth-goal-initiative-${item.id}`} />
            ))}
          </RowList>
        ) : (
          <p className="mt-3 text-[13px] text-sm-ink/65">No initiatives are attached to this goal yet. Ask {agentName} to plan against it.</p>
        )}
      </Panel>
      <div className="flex flex-wrap items-center gap-3">
        <button type="button" className={PRIMARY_BUTTON} disabled={askDisabled} onClick={() => onAsk?.(draft)} data-testid="growth-goal-ask"><MessageSquare size={15} /> Ask {agentName} to re-plan this goal</button>
        {askDisabled ? <span className="text-xs text-sm-ink/60">Chat is not available right now.</span> : null}
      </div>
    </GrowthDialog>
  );
}

// When an open item's status changes, the next step's button appears where the last one was (Approve becomes Mark
// done), so the second click of a double-click could land on it. Its actions wait a moment before taking clicks.
const SETTLE_MS = 700;
function useSettling(id, status) {
  const seen = useRef({ id, status });
  const [settling, setSettling] = useState(false);
  // A layout effect, so the buttons are already disabled when the new status is first painted.
  useLayoutEffect(() => {
    const changed = seen.current.id === id && seen.current.status !== status;
    seen.current = { id, status };
    if (!changed) return undefined;
    setSettling(true);
    const timer = setTimeout(() => setSettling(false), SETTLE_MS);
    return () => clearTimeout(timer);
  }, [id, status]);
  return settling;
}

/** One initiative: what, why, who, when, its history, and the actions its state allows. */
export function InitiativeDialog({ open, onClose, initiative, goal, activity = [], canAct = false, busy = false, actions, agentName, onAsk, askDisabled = false }) {
  const settling = useSettling(initiative?.id ?? null, initiative?.status ?? null);
  if (!initiative) return null;
  const waiting = busy || settling;
  const status = initiative.status;
  const when = initiative.due ? `Due ${fmtDayShort(initiative.due)}` : initiative.weekStart ? `Week of ${fmtDayShort(initiative.weekStart)}` : "No date";
  const history = activity.filter((entry) => entry.refId === initiative.id);
  const draft = `About the initiative "${initiative.title}"${goal ? ` (goal: ${goal.label})` : ""}: ${initiative.summary || ""} Status: ${INITIATIVE_STATUS[status]?.label || status}. What is the next step, and is anything blocking it?`;
  return (
    <GrowthDialog open={open} onClose={onClose} testId="growth-initiative-dialog" eyebrow={`Initiative · ${goal ? goal.label : "No goal attached"}`} title={initiative.title} description={`${ownerWord(initiative.owner, agentName)} · ${when}${initiative.expectedImpactCents ? ` · est. ${fmtUsdCents(initiative.expectedImpactCents)}` : ""}`} pill={<HeroPill tone={INITIATIVE_PILL[status] || "quiet"} dot={status === "done"}>{(INITIATIVE_STATUS[status]?.label || status).toUpperCase()}</HeroPill>}>
      {status === "waiting_on_you" ? <SectionNote tone="warn" testId="growth-initiative-waiting">{agentName} is waiting for your go-ahead on this one.</SectionNote> : null}
      <Panel>
        <PanelTitle>What and why</PanelTitle>
        <p className="mt-2 text-[13px] leading-relaxed text-sm-ink/80">{initiative.summary || "No summary yet."}</p>
        {initiative.why ? <p className="mt-2 text-[13px] leading-relaxed text-sm-ink/65"><span className="font-bold text-sm-ink">Why it is in the plan. </span>{initiative.why}</p> : null}
        {initiative.expectedImpactText ? <p className="mt-2 text-xs text-sm-ink/60">Impact estimate: {initiative.expectedImpactText}</p> : null}
        {initiative.sourceGuide ? <p className="mt-2 text-xs text-sm-ink/60">From the guide "{initiative.sourceGuide}".</p> : null}
      </Panel>
      <Panel>
        <StatGrid stats={[{ label: "Owner", value: ownerWord(initiative.owner, agentName) }, { label: "When", value: when }, { label: "Expected impact", value: initiative.expectedImpactCents ? fmtUsdCents(initiative.expectedImpactCents) : "—", sub: initiative.expectedImpactCents ? "estimate" : "Not estimated" }]} />
      </Panel>
      <Panel>
        <PanelTitle aside={<span className="text-xs text-sm-ink/65">{history.length ? `${history.length} entries` : null}</span>}>History</PanelTitle>
        {history.length ? (
          <RowList>
            {history.map((entry) => <ListRow key={entry.id} icon={CheckCircle2} tone="quiet" title={entry.text} text={fmtStamp(entry.at)} />)}
          </RowList>
        ) : <p className="mt-3 text-[13px] text-sm-ink/65">Created {fmtStamp(initiative.createdAt)} by {initiative.createdBy}.</p>}
      </Panel>
      <div className="flex flex-wrap items-center gap-2.5">
        {canAct && status === "waiting_on_you" ? <button type="button" className={PRIMARY_BUTTON} disabled={waiting} onClick={() => actions.approveInitiative(initiative.id)} data-testid="growth-initiative-approve">Approve</button> : null}
        {canAct && (status === "next" || status === "in_progress") ? <button type="button" className={PRIMARY_BUTTON} disabled={waiting} onClick={() => actions.completeInitiative(initiative.id)} data-testid="growth-initiative-done">Mark done</button> : null}
        {canAct && status !== "parked" && status !== "done" ? <button type="button" className={GHOST_BUTTON} disabled={waiting} onClick={() => actions.parkInitiative(initiative.id)} data-testid="growth-initiative-park">Park</button> : null}
        {canAct && (status === "parked" || status === "done") ? <button type="button" className={GHOST_BUTTON} disabled={waiting} onClick={() => actions.reopenInitiative(initiative.id)} data-testid="growth-initiative-reopen">Back into the plan</button> : null}
        <button type="button" className={GHOST_BUTTON} disabled={askDisabled} onClick={() => onAsk?.(draft)} data-testid="growth-initiative-ask"><MessageSquare size={14} /> Open in Chat</button>
        {!canAct ? <span className="text-xs text-sm-ink/60">Your role can read the plan but not act on it.</span> : null}
      </div>
    </GrowthDialog>
  );
}

/** One test: hypothesis, result, decision and the lesson. */
export function ExperimentDialog({ open, onClose, experiment, agentName, onAsk, askDisabled = false }) {
  if (!experiment) return null;
  const status = experiment.status;
  const draft = `About the test "${experiment.title}" (${experiment.channel || "channel not set"}): hypothesis "${experiment.hypothesis || ""}". Status ${EXPERIMENT_STATUS[status]?.label || status}${experiment.resultText ? `, result ${experiment.resultText}` : ""}. What did we learn, and what should we test next?`;
  return (
    <GrowthDialog open={open} onClose={onClose} testId="growth-experiment-dialog" eyebrow={`Experiment · ${experiment.channel || "Channel not set"}`} title={experiment.title} description={experiment.scope || experiment.metric || "Test"} pill={<HeroPill tone={EXPERIMENT_PILL[status] || "quiet"} dot={status === "running"}>{(EXPERIMENT_STATUS[status]?.label || status).toUpperCase()}</HeroPill>}>
      <Panel>
        <StatGrid stats={[{ label: "Started", value: experiment.startedOn ? fmtDayShort(experiment.startedOn) : "—" }, { label: status === "running" ? "Metric" : "Decided", value: status === "running" ? experiment.metric || "—" : experiment.decidedOn ? fmtDayShort(experiment.decidedOn) : "—" }, { label: "Confidence", value: fmtPercent(experiment.confidencePercent), sub: experiment.confidencePercent != null && experiment.confidencePercent < 90 && status === "running" ? "Below the 90% bar" : null, tone: experiment.confidencePercent != null && experiment.confidencePercent < 90 && status === "running" ? "warn" : undefined }]} />
      </Panel>
      <Panel>
        <PanelTitle>Hypothesis</PanelTitle>
        <p className="mt-2 text-[13px] leading-relaxed text-sm-ink/80">{experiment.hypothesis || "Not written yet."}</p>
      </Panel>
      <Panel>
        <PanelTitle aside={experiment.decision ? <Chip tone="ok">{experiment.decision}</Chip> : null}>Result</PanelTitle>
        <p className="mt-2 text-[13px] leading-relaxed text-sm-ink/80">{experiment.resultText || (status === "idea" ? "Not started." : "No result recorded yet.")}{experiment.liftPercent != null ? ` (${experiment.liftPercent >= 0 ? "+" : ""}${experiment.liftPercent}% on ${experiment.metric || "the metric"})` : ""}</p>
        {experiment.evidence ? <p className="mt-2 text-xs text-sm-ink/60">Evidence: {experiment.evidence}</p> : null}
      </Panel>
      <Panel>
        <PanelTitle>Lesson</PanelTitle>
        <p className="mt-2 text-[13px] leading-relaxed text-sm-ink/80">{experiment.lesson || `${agentName} has not written the lesson yet.`}</p>
      </Panel>
      <div><button type="button" className={PRIMARY_BUTTON} disabled={askDisabled} onClick={() => onAsk?.(draft)} data-testid="growth-experiment-ask"><MessageSquare size={15} /> Ask {agentName} about this test</button></div>
    </GrowthDialog>
  );
}

/** The agent's open questions with an answer box each; answered ones fold below. */
export function QuestionsDialog({ open, onClose, questions = [], canAct = false, busyKey = null, onAnswer, onSkip, agentName }) {
  const [drafts, setDrafts] = useState({});
  const [showAnswered, setShowAnswered] = useState(false);
  const openQuestions = useMemo(() => questions.filter((question) => question.status === "open"), [questions]);
  const answered = useMemo(() => questions.filter((question) => question.status === "answered"), [questions]);
  useEffect(() => { if (!open) setDrafts({}); }, [open]);
  return (
    <GrowthDialog open={open} onClose={onClose} testId="growth-questions-dialog" eyebrow="Questions for you" title={openQuestions.length ? `${openQuestions.length} open question${openQuestions.length === 1 ? "" : "s"}` : "Nothing open"} description={`Facts only you know. Answers go into ${agentName}'s context before it plans or writes.`} pill={<HeroPill tone={openQuestions.length ? "warn" : "on"} dot={!openQuestions.length}>{openQuestions.length ? `${openQuestions.length} OPEN` : "ALL ANSWERED"}</HeroPill>}>
      {openQuestions.length ? openQuestions.map((question) => {
        const draft = drafts[question.id] || "";
        const busy = busyKey === `question:${question.id}`;
        return (
          <Panel key={question.id} data-testid={`growth-question-${question.id}`}>
            <div className="flex items-start gap-3">
              <span className="mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] bg-[#F59E0B]/[0.12] text-[#B45309]"><CircleHelp size={16} /></span>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-bold leading-snug text-sm-ink">{question.question}</div>
                {question.why ? <div className="mt-1 text-xs leading-relaxed text-sm-ink/65">{question.why}</div> : null}
                {question.usedBy ? <div className="mt-1 text-xs text-sm-ink/55">Used by: {question.usedBy}</div> : null}
                {canAct ? (
                  <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-start">
                    <label className="sr-only" htmlFor={`growth-answer-${question.id}`}>Your answer</label>
                    <textarea id={`growth-answer-${question.id}`} maxLength={GROWTH_TEXT_LIMITS.prose} value={draft} disabled={busy} onChange={(event) => setDrafts((current) => ({ ...current, [question.id]: event.target.value }))} rows={2} placeholder="Type your answer…" className="min-h-[40px] flex-1 rounded-lg border border-sm-ink/15 bg-white px-3 py-2 text-[13px] text-sm-ink placeholder:text-sm-ink/45 focus:border-sm-accent focus:outline-none focus:ring-2 focus:ring-sm-accent/30" />
                    <div className="flex shrink-0 items-center gap-2">
                      <button type="button" className={SMALL_PRIMARY} disabled={busy || !draft.trim()} onClick={() => onAnswer(question.id, draft.trim())} data-testid={`growth-answer-send-${question.id}`}>Answer</button>
                      <button type="button" className={SMALL_GHOST} disabled={busy} onClick={() => onSkip(question.id)}>Not sure</button>
                    </div>
                  </div>
                ) : <p className="mt-2 text-xs text-sm-ink/60">Your role can read questions but not answer them.</p>}
              </div>
            </div>
          </Panel>
        );
      }) : (
        <Panel>
          <div className="flex items-center gap-3"><span className="inline-flex h-9 w-9 items-center justify-center rounded-[10px] bg-[#22C55E]/10 text-[#16A34A]"><CheckCircle2 size={17} /></span><div><div className="text-sm font-bold text-sm-ink">Nothing to answer</div><div className="text-xs text-sm-ink/65">{agentName} will ask here when it needs something only you know.</div></div></div>
        </Panel>
      )}
      {answered.length ? (
        <div>
          <button type="button" className={TEXT_LINK_CLASS} onClick={() => setShowAnswered((value) => !value)} data-testid="growth-questions-answered-toggle">{showAnswered ? "Hide" : "Show"} {answered.length} answered</button>
          {showAnswered ? (
            <Panel className="mt-3">
              <RowList>
                {answered.map((question) => <ListRow key={question.id} icon={CheckCircle2} tone="ok" title={question.question} text={`${question.answer} · ${question.answeredAt ? fmtStamp(question.answeredAt) : ""}`} />)}
              </RowList>
            </Panel>
          ) : null}
        </div>
      ) : null}
    </GrowthDialog>
  );
}
const TEXT_LINK_CLASS = "text-xs font-bold text-sm-primary hover:text-sm-ink";

const TARGET_ROWS = [
  { key: "revenue", label: "Revenue", unit: "usd", hint: "Net sales this month" },
  { key: "orders", label: "Orders", unit: "count", hint: "Orders this month" },
  { key: "new_customers", label: "New customers", unit: "count", hint: "First orders this month" },
  { key: "mer", label: "Blended MER", unit: "ratio", hint: "Revenue ÷ all ad spend" },
  { key: "email_share", label: "Email + SMS share", unit: "percent", hint: "Share of revenue attributed to Klaviyo" },
  { key: "aov", label: "Average order", unit: "usd", hint: "Revenue ÷ orders" },
];
const UNIT_PREFIX = { usd: "$", ratio: "×", percent: "%", count: "" };

/**
 * What Save sends, worked out against the targets as they were when the dialog opened: only the six lines the dialog shows,
 * only a line whose number changed (so a target the agent moved meanwhile is not overwritten with the old number), and a
 * removal only for a line that had a target and is now blank. A line that does not read as a number is an error, never a drop.
 */
export function targetChanges(goals, values) {
  const saved = new Map(goals.map((goal) => [goal.key, goal]));
  const changes = { goals: [], remove: [], errors: {} };
  for (const row of TARGET_ROWS) {
    const current = saved.get(row.key);
    // A line the owner left as it was is neither checked nor sent: the agent may have set a number these checks
    // would refuse (412.5 orders), and that must not block saving the lines the owner did change.
    if (untouched(current, values[row.key])) continue;
    const { value, error } = parseTarget(values[row.key], row.unit);
    if (error) changes.errors[row.key] = error;
    else if (value == null) { if (current) changes.remove.push(row.key); }
    else if (!current || current.target !== value) changes.goals.push({ key: row.key, label: row.label, target: value, unit: row.unit });
  }
  return changes;
}
const untouched = (goal, raw) => Boolean(goal) && String(raw ?? "").trim() === String(goal.target);
const joinLabels = (labels) => (labels.length < 2 ? labels.join("") : `${labels.slice(0, -1).join(", ")} and ${labels[labels.length - 1]}`);

/**
 * Set or change the month's targets. Blank means no target for that line. Each line shows what the store has done so far this
 * month and over the last 30 days (from the daily metrics), with a one-click rounded starting point; a line the store does not
 * measure yet says so instead of showing zero. Removing a target asks first.
 */
export function EditTargetsDialog({ open, onClose, goals = [], period, busy = false, onSave, agentName, metrics = [], today = null, month = null }) {
  const [values, setValues] = useState({});
  const [errors, setErrors] = useState({});
  // The month and its targets as they were when the dialog opened. The page follows today, so a dialog left open over
  // midnight at the end of a month must still show and save the month the owner was editing.
  const [opened, setOpened] = useState(null);
  const [confirming, setConfirming] = useState(null);
  const wasOpen = useRef(false);
  const confirmHeading = useRef(null);
  const saveButton = useRef(null);
  const wasConfirming = useRef(false);
  const monthRows = useMemo(() => (today && month ? metricsBetween(metrics, month.start, shiftDay(today, -1)) : []), [metrics, today, month]);
  const trailingRows = useMemo(() => (today ? metricsBetween(metrics, shiftDay(today, -30), shiftDay(today, -1)) : []), [metrics, today]);
  // Fill the boxes when the dialog opens, never again while it is open: the page re-reads its data on focus, on tab
  // switches and every minute, and filling the boxes then wiped what the owner was typing.
  useEffect(() => {
    if (open && !wasOpen.current) {
      const next = {};
      for (const goal of goals) next[goal.key] = String(goal.target);
      setValues(next);
      setErrors({});
      setConfirming(null);
      setOpened({ period, goals });
    } else if (!open && wasOpen.current) setOpened(null);
    wasOpen.current = open;
  }, [open, goals, period]);
  // The question's buttons are disabled while a save runs, so focus falls out of them: it goes back to the question when
  // the save fails and the question is still open, and to Save when the question closes. An effect, not an animation
  // frame, so it also happens in a tab that is not drawing.
  useEffect(() => {
    if (confirming && !busy) confirmHeading.current?.focus();
    else if (!confirming && wasConfirming.current) saveButton.current?.focus();
    wasConfirming.current = Boolean(confirming);
  }, [confirming, busy]);
  const snapshot = opened || { period, goals };
  // Targets the dialog does not edit are listed as they are now while the page is still on the same month.
  const shown = snapshot.period === period ? goals : snapshot.goals;
  const others = shown.filter((goal) => !TARGET_ROWS.some((row) => row.key === goal.key));
  const errorCount = Object.values(errors).filter(Boolean).length;
  const edit = (key, value) => {
    setValues((current) => ({ ...current, [key]: value }));
    setErrors((current) => ({ ...current, [key]: null }));
  };
  const check = (row) => setErrors((current) => ({ ...current, [row.key]: untouched(snapshot.goals.find((goal) => goal.key === row.key), values[row.key]) ? null : parseTarget(values[row.key], row.unit).error }));
  const save = () => {
    const changes = targetChanges(snapshot.goals, values);
    const invalid = Object.keys(changes.errors);
    setErrors(changes.errors);
    if (invalid.length) { document.getElementById(`growth-target-${invalid[0]}`)?.focus(); return; }
    if (!changes.goals.length && !changes.remove.length) { onClose(); return; }
    if (changes.remove.length) { setConfirming(changes); return; }
    onSave(snapshot.period, changes.goals, changes.remove);
  };
  const keepEditing = () => setConfirming(null);
  const removedLabels = confirming ? confirming.remove.map((key) => TARGET_ROWS.find((row) => row.key === key)?.label || key) : [];
  const one = removedLabels.length === 1;
  return (
    <GrowthDialog open={open} onClose={onClose} testId="growth-targets-dialog" eyebrow="Targets" title={fmtMonth(snapshot.period)} description={`The numbers ${agentName} plans against and reports on. Leave a line blank to skip it.`} pill={<HeroPill tone="quiet"><Target size={12} className="mr-1" />{shown.length} SET</HeroPill>}>
      <Panel>
        <div className="flex flex-col divide-y divide-sm-ink/[0.08]">
          {TARGET_ROWS.map((row) => {
            const error = errors[row.key];
            return (
              <div key={row.key} className="grid grid-cols-[minmax(0,1fr)_150px] items-center gap-x-3 gap-y-1.5 py-2.5">
                <label htmlFor={`growth-target-${row.key}`} className="min-w-0">
                  <span className="block text-sm font-bold text-sm-ink">{row.label}</span>
                  <span className="block text-xs text-sm-ink/60">{row.hint}</span>
                  <TargetNow row={row} now={goalActual(row.key, monthRows)} trailing={goalActual(row.key, trailingRows)} disabled={busy || Boolean(confirming)} onUse={(value) => edit(row.key, String(value))} />
                </label>
                <div className="flex items-center gap-1.5">
                  {row.unit === "usd" ? <span className="text-sm text-sm-ink/60">$</span> : null}
                  <input id={`growth-target-${row.key}`} inputMode="decimal" value={values[row.key] ?? ""} disabled={busy || Boolean(confirming)} onChange={(event) => edit(row.key, event.target.value)} onBlur={() => check(row)} aria-invalid={error ? true : undefined} aria-describedby={error ? `growth-target-error-${row.key}` : undefined} placeholder="—" className={`h-9 w-full rounded-lg border bg-white px-3 text-right text-sm font-bold text-sm-ink tabular-nums focus:outline-none focus:ring-2 ${error ? "border-[#B42318] focus:ring-[#B42318]/30" : "border-sm-ink/15 focus:border-sm-accent focus:ring-sm-accent/30"}`} />
                  {row.unit !== "usd" && UNIT_PREFIX[row.unit] ? <span className="w-4 text-sm text-sm-ink/60">{UNIT_PREFIX[row.unit]}</span> : null}
                </div>
                {error ? <p id={`growth-target-error-${row.key}`} className="col-span-2 text-xs font-bold text-[#B42318]">{error}</p> : null}
              </div>
            );
          })}
        </div>
      </Panel>
      {others.length ? <p className="text-xs leading-relaxed text-sm-ink/65" data-testid="growth-targets-other">Also set for {fmtMonth(snapshot.period)}: {others.map((goal) => `${goal.label} ${fmtTarget(goal)}`).join(", ")}. Ask {agentName} in Chat to change {others.length === 1 ? "it" : "these"}.</p> : null}
      {confirming ? (
        <div className="rounded-2xl border border-[#F59E0B]/40 bg-[#F59E0B]/[0.08] p-4" role="group" aria-labelledby="growth-targets-confirm-title" data-testid="growth-targets-confirm">
          <h3 id="growth-targets-confirm-title" ref={confirmHeading} tabIndex={-1} className="text-sm font-bold text-sm-ink focus:outline-none">Remove the {joinLabels(removedLabels)} target{one ? "" : "s"} for {fmtMonth(snapshot.period)}?</h3>
          <p className="mt-1 text-[13px] text-sm-ink/75">{agentName} stops planning against {one ? "it" : "them"}. You can set {one ? "it" : "them"} again later.</p>
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <button type="button" className={PRIMARY_BUTTON} disabled={busy} onClick={() => onSave(snapshot.period, confirming.goals, confirming.remove)} data-testid="growth-targets-remove">Remove and save</button>
            <button type="button" className={GHOST_BUTTON} disabled={busy} onClick={keepEditing} data-testid="growth-targets-keep">Keep editing</button>
          </div>
        </div>
      ) : (
        <div className="flex flex-wrap items-center gap-3">
          <button type="button" ref={saveButton} className={PRIMARY_BUTTON} disabled={busy} onClick={save} data-testid="growth-targets-save">Save targets</button>
          <button type="button" className={GHOST_BUTTON} disabled={busy} onClick={onClose} data-testid="growth-targets-cancel">Cancel</button>
          {errorCount ? <span role="alert" className="text-xs font-bold text-[#B42318]">{errorCount === 1 ? "Fix the highlighted target before saving." : `Fix the ${errorCount} highlighted targets before saving.`}</span> : null}
        </div>
      )}
    </GrowthDialog>
  );
}

/** The store's own numbers for a target line, and a rounded starting point to fill in. */
function TargetNow({ row, now, trailing, disabled, onUse }) {
  const suggestion = suggestTarget(row.key, trailing ?? now);
  if (now == null && trailing == null) return <span className="mt-1 block text-xs text-sm-ink/55" data-testid={`growth-target-now-${row.key}`}>Not measured yet</span>;
  return (
    <span className="mt-1 block text-xs text-sm-ink/75" data-testid={`growth-target-now-${row.key}`}>
      So far this month <b className="text-sm-ink">{fmtByUnit(now, row.unit)}</b> · last 30 days <b className="text-sm-ink">{fmtByUnit(trailing, row.unit)}</b>
      {suggestion != null ? <button type="button" disabled={disabled} onClick={(event) => { event.preventDefault(); onUse(suggestion); }} className="ml-2 font-bold text-sm-primary hover:text-sm-ink disabled:opacity-50" data-testid={`growth-target-use-${row.key}`}>Use {fmtByUnit(suggestion, row.unit)}</button> : null}
    </span>
  );
}

export const GOAL_ICON = Target;
export const EXPERIMENT_ICON = FlaskConical;
